import type { MonthEntry, MonthRecord, Requirement, StudentRow } from './assignmentMonitoringApi';

// Dates arrive as YYYY-MM-DD and months as YYYY-MM. They are built at local
// noon so the calendar day never shifts with the viewer's time zone.
function parts(value: string): [number, number, number] {
  const [y = 0, m = 1, d = 1] = value.split('-').map(Number);
  return [y, m, d];
}

function dateOf(date: string): Date {
  const [y, m, d] = parts(date);
  return new Date(y, m - 1, d, 12);
}

export function formatWeekday(date: string): string {
  return dateOf(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatMonthDay(date: string): string {
  return dateOf(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function formatLong(date: string): string {
  return dateOf(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

export function monthName(month: string): string {
  return dateOf(`${month}-01`).toLocaleDateString('en-US', { month: 'long' });
}

export function monthLabel(month: string): string {
  return dateOf(`${month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export function firstDay(month: string): string {
  return `${month}-01`;
}

export function lastDay(month: string): string {
  const [y, m] = parts(month);
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
}

export function nextMonth(month: string): string {
  const [y, m] = parts(month);
  const d = new Date(y, m, 1, 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function plural(n: number, word: string): string {
  if (n === 1) return `1 ${word}`;
  return `${n} ${word.endsWith('s') ? `${word}es` : `${word}s`}`;
}

export function pct(a: number, b: number): number {
  return b ? Math.round((100 * a) / b) : 0;
}

export const MAX_ASSIGN = 99;

export function requirementError(assign: string, score: string): string | null {
  if (!/^\d+$/.test(assign) || !/^\d+$/.test(score)) return 'Enter whole numbers.';
  const a = Number(assign);
  const s = Number(score);
  if (a < 1) return 'Each student must receive at least 1 assignment.';
  if (a > MAX_ASSIGN) return `Each student can receive at most ${MAX_ASSIGN} assignments.`;
  if (s > a) return `Can’t be more than the ${plural(a, 'assignment')} each student must receive.`;
  return null;
}

export interface Chip {
  label: string;
  tone: 'indigo' | 'grey' | 'red';
}

export type MonthLike = Pick<MonthEntry, 'month' | 'phase' | 'closeDate' | 'skipped' | 'window'>;

export function monthStateLabel(entry: MonthLike): string {
  if (entry.skipped) return 'Skipped';
  if (entry.phase === 'final') return 'Final';
  if (entry.phase === 'closing') return `Closing ${formatMonthDay(entry.closeDate)}`;
  return 'In progress';
}

export function monthChip(entry: MonthLike): Chip {
  if (entry.skipped) return { label: 'Skipped', tone: 'grey' };
  if (entry.phase === 'closing') return { label: `Closing ${formatWeekday(entry.closeDate)}`, tone: 'indigo' };
  if (entry.phase === 'final') return { label: 'Final', tone: 'grey' };
  return { label: 'In progress', tone: 'indigo' };
}

export function requirementPhrase(req: Requirement): string {
  return `assign ${req.assign} and score ${req.score} per student`;
}

export function recordSubline(entry: MonthLike, req: Requirement, prefix?: string): string {
  const lead = prefix ? `${prefix} · ` : '';
  if (entry.skipped && entry.window) {
    return `${lead}${entry.window.kind} window · ${formatWeekday(entry.window.start)} – ${formatWeekday(entry.window.close)}`;
  }
  const phrase = requirementPhrase(req);
  const rule = prefix ? phrase : phrase.charAt(0).toUpperCase() + phrase.slice(1);
  const tail =
    entry.phase === 'open'
      ? `closes ${formatWeekday(lastDay(entry.month))}`
      : entry.phase === 'closing'
        ? 'counts still changing'
        : `closed ${formatWeekday(entry.closeDate)}`;
  return `${lead}${rule} · ${tail}`;
}

export function studentStatusLabel(row: StudentRow): string {
  switch (row.status) {
    case 'notAssigned':
      return `Not assigned · ${row.short} short`;
    case 'notSubmitted':
      return `Not submitted · assigned ${row.lastAssigned ? formatMonthDay(row.lastAssigned) : '—'}`;
    case 'notScored':
      return `Not scored · submitted ${row.lastSubmitted ? formatMonthDay(row.lastSubmitted) : '—'}`;
    default:
      return 'Met';
  }
}

export function shortSchool(name: string): string {
  return name.replace(/ School$/, '');
}

export function schoolsLabel(names: string[]): string {
  const [first = '', second = ''] = names;
  if (names.length === 1) return shortSchool(first);
  if (names.length === 2) return `${shortSchool(first)} and ${shortSchool(second)}`;
  return plural(names.length, 'school');
}

// A name that starts like a formula is quoted so a spreadsheet shows it as
// text instead of evaluating it.
function csvCell(value: string): string {
  const text = /^[=+\-@\t\r\n]/.test(value) ? `'${value}` : value;
  return /[",\n\r\t]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

// Teacher level only: one line per teacher and school, no student names.
export function buildRecordCsv(record: MonthRecord): string {
  const schoolName = new Map((record.schools ?? []).map((s) => [s.id, s.name]));
  const lines = ['Teacher,School,Students met,Met'];
  for (const teacher of record.teachers ?? []) {
    for (const school of teacher.schools) {
      lines.push(
        [
          teacher.name,
          schoolName.get(school.schoolId) ?? school.schoolId,
          `${school.met} of ${school.students}`,
          school.metAll ? 'Met' : 'Not met',
        ]
          .map(csvCell)
          .join(','),
      );
    }
  }
  return `${lines.join('\n')}\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
